import SearchRounded from '@mui/icons-material/SearchRounded';
import {
  Box,
  Button,
  Card,
  Checkbox,
  InputAdornment,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState, type ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  /** Hidden below this breakpoint (keeps phone tables readable). */
  hideBelow?: 'sm' | 'md' | 'lg';
}

export interface PageInfo {
  page: number;
  pageSize: number;
  total: number;
}

/**
 * The standard list: a card with an optional toolbar (search + filters), a table with skeleton rows,
 * an empty state, and server-side pagination (pages are 1-based, as in the API).
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  page,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  empty,
  toolbar,
  selected,
  onSelectedChange,
  selectionActions,
}: {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  page?: PageInfo;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  toolbar?: ReactNode;
  /**
   * Row checkboxes: the ids picked (kept across pages until cleared). While any are picked a bar
   * shows how many with `selectionActions` (e.g. Export) and Clear.
   */
  selected?: string[];
  onSelectedChange?: (ids: string[]) => void;
  selectionActions?: ReactNode;
}) {
  const selectable = !!selected && !!onSelectedChange;
  const setPicked = (ids: string[]) => onSelectedChange?.(ids);
  const picked = new Set(selected);
  const pageKeys = rows?.map(rowKey) ?? [];
  const pageOn = pageKeys.filter((k) => picked.has(k)).length;
  const toggle = (key: string) => {
    const next = new Set(picked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setPicked([...next]);
  };
  const togglePage = () => {
    const next = new Set(picked);
    if (pageKeys.length > 0 && pageOn === pageKeys.length) pageKeys.forEach((k) => next.delete(k));
    else pageKeys.forEach((k) => next.add(k));
    setPicked([...next]);
  };
  const display = (c: Column<T>) =>
    c.hideBelow ? { display: { xs: 'none', [c.hideBelow]: 'table-cell' } } : {};
  return (
    <Card>
      {selectable && picked.size > 0 && (
        <Stack
          direction="row"
          spacing={1.5}
          role="region"
          aria-label="Selected rows"
          sx={{
            px: 2,
            py: 1.25,
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'action.selected',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
            {picked.size} selected
          </Typography>
          {selectionActions}
          <Button size="small" onClick={() => setPicked([])}>
            Clear
          </Button>
        </Stack>
      )}
      {toolbar && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ p: 2, borderBottom: 1, borderColor: 'divider', alignItems: { sm: 'center' } }}
        >
          {toolbar}
        </Stack>
      )}
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={pageKeys.length > 0 && pageOn === pageKeys.length}
                    indeterminate={pageOn > 0 && pageOn < pageKeys.length}
                    disabled={pageKeys.length === 0}
                    onChange={togglePage}
                    slotProps={{ input: { 'aria-label': 'Select all rows on this page' } }}
                  />
                </TableCell>
              )}
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align} sx={{ width: c.width, ...display(c) }}>
                  {c.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 6 }, (_, i) => (
                <TableRow key={`s${String(i)}`}>
                  {selectable && <TableCell padding="checkbox" />}
                  {columns.map((c) => (
                    <TableCell key={c.key} sx={display(c)}>
                      <Skeleton
                        width={c.align === 'right' ? '50%' : '70%'}
                        sx={{ ml: c.align === 'right' ? 'auto' : 0 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!loading &&
              rows?.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  hover
                  selected={selectable && picked.has(rowKey(row))}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  // Clickable rows open with Enter or Space too, like a link.
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:last-child td': { borderBottom: 0 },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: -2,
                    },
                  }}
                >
                  {selectable && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={picked.has(rowKey(row))}
                        onChange={() => toggle(rowKey(row))}
                        onKeyDown={(e) => e.stopPropagation()}
                        slotProps={{ input: { 'aria-label': 'Select row' } }}
                      />
                    </TableCell>
                  )}
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align} sx={display(c)}>
                      {c.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && rows?.length === 0 && <Box>{empty}</Box>}
      {page && onPageChange && page.total > 0 && (
        <TablePagination
          component="div"
          count={page.total}
          page={page.page - 1}
          rowsPerPage={page.pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => onPageChange(p + 1)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          sx={{ borderTop: 1, borderColor: 'divider' }}
        />
      )}
    </Card>
  );
}

/** Debounced search box for list toolbars. */
export function SearchField({
  value,
  onChange,
  placeholder = 'Search…',
  delay = 300,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  delay?: number;
}) {
  const [text, setText] = useState(value);
  useEffect(() => {
    if (text === value) return;
    const t = setTimeout(() => onChange(text), delay);
    return () => clearTimeout(t);
  }, [text, value, onChange, delay]);
  return (
    <TextField
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder={placeholder}
      sx={{ maxWidth: { sm: 340 } }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
    />
  );
}
